import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export const ComplianceTab = () => {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState(null);
  const [history, setHistory] = useState([]);
  const [takedowns, setTakedowns] = useState([]);
  const [confirmText, setConfirmText] = useState("");

  const loadAll = async () => {
    try {
      const [t, h] = await Promise.all([
        api.get("/admin/takedowns"),
        api.get("/admin/compliance-audit"),
      ]);
      setTakedowns(t.data);
      setHistory(h.data);
      if (h.data && h.data[0]) setAudit(h.data[0]);
    } catch {
      /* ignore */
    }
  };
  useEffect(() => { loadAll(); }, []);

  const runAudit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/compliance-audit");
      setAudit(r.data);
      toast.success("Compliance audit complete");
      loadAll();
    } catch {
      toast.error("Audit failed");
    } finally {
      setBusy(false);
    }
  };

  const doTakedown = async () => {
    const q = query.trim();
    if (q.length < 4) return toast.error("Query must be at least 4 characters");
    if (confirmText !== q) return toast.error(`Type "${q}" to confirm`);
    setBusy(true);
    try {
      const r = await api.post("/admin/takedown", { query: q });
      toast.success(`Removed ${r.data.removed} articles`);
      setQuery("");
      setConfirmText("");
      loadAll();
    } catch {
      toast.error("Takedown failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-compliance-view">
      <div className="card-soft p-5">
        <h3 className="text-sm uppercase tracking-wider text-[#1A1A1A] mb-1">
          Content Removal / DMCA Takedown
        </h3>
        <p className="text-xs text-[#6B7280] mb-4">
          Search by source name or domain. Confirms removal by retyping the query string.
        </p>
        <div className="flex flex-col gap-2 max-w-xl">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. 24ora.com  or  Knipselkrant"
            className="px-3 py-2 rounded border border-[#EEEEEE] text-sm"
            data-testid="takedown-query-input"
          />
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={`Type ${query ? `"${query}"` : "the query"} again to confirm`}
            className="px-3 py-2 rounded border border-[#EEEEEE] text-sm"
            disabled={query.trim().length < 4}
            data-testid="takedown-confirm-input"
          />
          <button
            onClick={doTakedown}
            disabled={busy || query.trim().length < 4 || confirmText !== query.trim()}
            className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-[#E63946] hover:bg-[#cf2f3c] text-white text-sm disabled:opacity-50 w-fit"
            data-testid="takedown-execute-btn"
          >
            Delete all matching articles
          </button>
        </div>

        {takedowns.length > 0 && (
          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">
              Takedown Log
            </div>
            <ul className="space-y-1.5">
              {takedowns.map((t, i) => (
                <li key={i} className="text-[12px] text-[#6B7280] border-b border-[#EEEEEE] pb-1.5"
                    data-testid={`takedown-log-row-${i}`}>
                  <span className="text-[#1A2E35]">{t.at?.slice(0, 19)}</span> —
                  query <span className="text-[#0AADA0]">{t.query}</span> · removed{" "}
                  <span className="text-[#E63946]">{t.removed}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="card-soft p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm uppercase tracking-wider text-[#1A1A1A]">
            Legal Compliance Audit
          </h3>
          <button
            onClick={runAudit}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0AADA0] hover:bg-[#08988c] text-white text-xs disabled:opacity-50"
            data-testid="run-compliance-audit-btn"
          >
            <ShieldCheck size={13} /> Run audit
          </button>
        </div>

        {audit ? (
          <div data-testid="compliance-audit-report">
            <div className="text-[13px] text-[#1A2E35] mb-4">{audit.summary}</div>
            <ul className="space-y-2">
              {(audit.report || []).map((r, i) => (
                <li key={i} className="border border-[#EEEEEE] rounded p-3" data-testid={`compliance-check-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] text-[#1A2E35] font-medium">{r.check}</span>
                    <span
                      className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{
                        background:
                          r.status === "PASSED" ? "#3BB273"
                          : r.status === "FIXED" ? "#0AADA0"
                          : "#E63946",
                        color: "white",
                      }}
                    >
                      {r.status}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#6B7280] mt-1">{r.found}</div>
                  {r.fixed && (
                    <div className="text-[11px] text-[#0AADA0] mt-1">Fix: {r.fixed}</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="text-sm text-[#6B7280]">
            No audit has been run yet. Click &ldquo;Run audit&rdquo;.
          </div>
        )}

        {history.length > 1 && (
          <div className="mt-6 text-[11px] text-[#6B7280]" data-testid="compliance-audit-history">
            Previous audits: {history.length} runs on record.
            {(() => {
              const lastCron = history.find((h) => h.source === "cron");
              return lastCron ? (
                <span className="ml-2">
                  · Last automated run:{" "}
                  <span className="text-[#1A1A1A]">
                    {new Date(lastCron.ran_at).toLocaleString()}
                  </span>
                </span>
              ) : (
                <span className="ml-2 text-[#9CA3AF]">· Nightly cron: 03:00 America/Aruba</span>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};
